import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Image, MapPin, Phone, Check, CheckCheck, Loader2, Truck, Camera, Navigation, Mic, Play, Pause, Trash2, Languages } from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { chatApi, getUser } from '../lib/api';
import { useI18n } from '../lib/i18n';

const demoMessages = (load, user) => {
  const now = Date.now();
  const shipper = load.poster || 'Reliance Freight';
  const driver = load.driver || 'Sohan Lal';
  return [
    { id: 'd1', kind: 'text', text: `Hi ${driver}, thanks for taking this load. Please pick up before 10 AM.`, sender_name: shipper, sender_role: 'shipper', at: new Date(now - 5*3600000).toISOString(), read: true },
    { id: 'd2', kind: 'text', text: 'Namaste sir, main 9 baje pahunch jaunga.', sender_name: driver, sender_role: 'driver', at: new Date(now - 4.5*3600000).toISOString(), read: true, mine: true },
    { id: 'd3', kind: 'location', lat: 21.1458, lng: 79.0882, text: 'Currently at Nagpur toll plaza', sender_name: driver, sender_role: 'driver', at: new Date(now - 2*3600000).toISOString(), read: true, mine: true },
    { id: 'd4', kind: 'text', text: 'Great, keep me updated.', sender_name: shipper, sender_role: 'shipper', at: new Date(now - 1.8*3600000).toISOString(), read: true },
    { id: 'd5', kind: 'text', text: 'Sir, mile ke pass 30 min ka jam hai. Thoda late ho sakta hai.', sender_name: driver, sender_role: 'driver', at: new Date(now - 30*60000).toISOString(), read: false, mine: true },
  ];
};

const timeStr = (ts) => new Date(ts).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
const dateStr = (ts) => new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function VoiceBubble({ src, duration, transcript, transcriptLang, mine, t }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [showT, setShowT] = useState(false);
  const dur = Number(duration) || 0;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPos(el.currentTime || 0);
    const onEnd = () => { setPlaying(false); setPos(0); try { el.currentTime = 0; } catch (e) {} };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    return () => { el.removeEventListener('timeupdate', onTime); el.removeEventListener('ended', onEnd); };
  }, []);

  const toggle = () => {
    const el = audioRef.current; if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const fmt = (s) => { const m = Math.floor(s/60); const sec = Math.floor(s%60); return `${m}:${sec.toString().padStart(2,'0')}`; };
  const pct = dur > 0 ? Math.min(100, (pos / dur) * 100) : 0;

  return (
    <div className="px-2.5 pt-2 pb-1">
      <div className="flex items-center gap-2 min-w-[180px]">
        <button
          data-testid="voice-play-btn"
          onClick={toggle}
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${mine ? 'bg-[#00A651] text-white' : 'bg-[#075E54] text-white'}`}
        >
          {playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="h-1.5 bg-black/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#075E54] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-600 font-mono">{fmt(playing || pos > 0 ? pos : dur)}</span>
            <Mic className="w-3 h-3 text-[#00A651]" />
          </div>
        </div>
      </div>
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
      {transcript && (
        <div className="mt-1.5">
          <button
            data-testid="voice-transcript-toggle"
            onClick={() => setShowT(s => !s)}
            className="flex items-center gap-1 text-[10px] text-[#075E54] hover:underline"
          >
            <Languages className="w-3 h-3" />
            {showT ? t('transcript') : t('showTranscript')}
          </button>
          {showT && (
            <p data-testid="voice-transcript-text" className="text-xs text-gray-700 italic mt-1 border-l-2 border-[#00A651] pl-2 whitespace-pre-line">
              {transcript}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function LoadChat({ open, onClose, load }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const user = getUser();
  const isDriver = user?.role === 'driver';
  const { t, lang } = useI18n();

  // Voice recording state
  const [recording, setRecording] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);
  const elapsedTimerRef = useRef(null);
  const touchStartXRef = useRef(0);

  useEffect(() => {
    if (!open || !load) return;
    // Load from backend, fallback to demo
    chatApi.list(load.id).then(r => {
      if (r && r.length > 0) setMessages(r.map(m => ({ ...m, mine: m.sender_id === user?.id })));
      else setMessages(demoMessages(load, user));
    }).catch(() => setMessages(demoMessages(load, user)));

    const iv = setInterval(() => {
      chatApi.list(load.id).then(r => {
        if (r && r.length > 0) setMessages(prev => {
          if (r.length > prev.filter(m => !m.demo).length) return r.map(m => ({ ...m, mine: m.sender_id === user?.id }));
          return prev;
        });
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(iv);
  }, [open, load]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);

  const push = async (payload) => {
    const optimistic = { id: `t${Date.now()}`, ...payload, sender_name: user?.name || 'You', sender_role: user?.role || 'user', at: new Date().toISOString(), read: false, mine: true };
    setMessages(prev => [...prev, optimistic]);
    setSending(true);
    try {
      const r = await chatApi.send(load.id, payload);
      setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...r, mine: true } : m));
    } catch (e) {
      // Keep optimistic message; will retry on next load
    } finally { setSending(false); }
  };

  const sendText = () => {
    if (!input.trim()) return;
    push({ kind: 'text', text: input.trim() });
    setInput('');
  };

  const sendPhoto = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast({ title: 'Photo too large', description: 'Please use under 3MB.' }); return; }
    const reader = new FileReader();
    reader.onload = () => push({ kind: 'photo', photo: reader.result, text: '' });
    reader.readAsDataURL(file);
  };

  const sharePin = () => {
    if (!navigator.geolocation) { toast({ title: 'GPS not available' }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => push({ kind: 'location', lat: pos.coords.latitude, lng: pos.coords.longitude, text: 'Live location' }),
      () => toast({ title: 'GPS denied', description: 'Please allow location access.' })
    );
  };

  // ----- Voice recording (hold-to-record) -----
  const cleanupRecording = () => {
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(tr => tr.stop()); } catch (e) {}
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast({ title: 'Voice not supported', description: 'Your browser does not support voice recording.' });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      // Prefer webm/opus, fallback to mp4/whatever browser supports (Safari uses audio/mp4)
      let mime = '';
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
      for (const c of candidates) { if (window.MediaRecorder.isTypeSupported?.(c)) { mime = c; break; } }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      cancelledRef.current = false;
      setCancelled(false);
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const wasCancelled = cancelledRef.current;
        const chunks = audioChunksRef.current.slice();
        const durSec = Math.max(0.1, (Date.now() - startedAtRef.current) / 1000);
        cleanupRecording();
        setRecording(false);
        setElapsed(0);
        if (wasCancelled || chunks.length === 0) return;
        if (durSec < 0.6) { toast({ title: 'Too short', description: 'Hold longer to record.' }); return; }
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size > 8 * 1024 * 1024) { toast({ title: 'Voice note too large', description: 'Please keep under 2 minutes.' }); return; }
        // Convert to data URL
        const reader = new FileReader();
        reader.onload = async () => {
          const dataUrl = reader.result;
          // Optimistic voice message
          const optimistic = {
            id: `t${Date.now()}`, kind: 'voice', audio: dataUrl, duration: durSec,
            sender_name: user?.name || 'You', sender_role: user?.role || 'user',
            at: new Date().toISOString(), read: false, mine: true, transcribing: true,
          };
          setMessages(prev => [...prev, optimistic]);
          // Kick off transcription (best-effort)
          setTranscribing(true);
          let transcript = null; let transcriptLang = null;
          try {
            const r = await chatApi.transcribe(dataUrl, lang === 'hi' ? 'hi' : 'en');
            transcript = (r?.text || '').trim() || null;
            transcriptLang = r?.language || (lang === 'hi' ? 'hi' : 'en');
          } catch (e) {
            // Transcription is optional - continue without
          }
          setTranscribing(false);
          // Send to backend
          try {
            const saved = await chatApi.send(load.id, { kind: 'voice', audio: dataUrl, duration: durSec, transcript, transcript_lang: transcriptLang, text: transcript || '' });
            setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...saved, mine: true } : m));
          } catch (e) {
            // Keep optimistic with transcript
            setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, transcript, transcript_lang: transcriptLang, transcribing: false } : m));
          }
        };
        reader.readAsDataURL(blob);
      };
      mr.start();
      startedAtRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => {
        const s = (Date.now() - startedAtRef.current) / 1000;
        setElapsed(s);
        if (s >= 120) { // auto-stop at 2 min
          stopRecording(false);
        }
      }, 200);
    } catch (e) {
      toast({ title: 'Mic denied', description: 'Please allow microphone access to record.' });
      cleanupRecording();
      setRecording(false);
    }
  };

  const stopRecording = (cancel = false) => {
    cancelledRef.current = !!cancel;
    setCancelled(!!cancel);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try { mr.stop(); } catch (e) {}
    } else {
      cleanupRecording();
      setRecording(false);
      setElapsed(0);
    }
  };

  const onMicTouchStart = (e) => { touchStartXRef.current = e.touches?.[0]?.clientX ?? 0; startRecording(); };
  const onMicTouchMove = (e) => {
    if (!recording) return;
    const x = e.touches?.[0]?.clientX ?? 0;
    if (touchStartXRef.current && touchStartXRef.current - x > 80) {
      stopRecording(true);
    }
  };
  const onMicTouchEnd = () => { if (recording) stopRecording(false); };

  useEffect(() => () => cleanupRecording(), []);

  const fmtElapsed = (s) => { const m = Math.floor(s/60); const sec = Math.floor(s%60); return `${m}:${sec.toString().padStart(2,'0')}`; };
  // ----- end voice recording -----

  if (!open || !load) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center sm:justify-end sm:pr-6 p-0 sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full sm:w-[420px] h-full sm:h-[640px] sm:rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#075E54] text-white p-3 flex items-center gap-3">
          <button onClick={onClose} className="sm:hidden"><X className="w-5 h-5" /></button>
          <div className="w-10 h-10 rounded-full bg-[#00A651] flex items-center justify-center flex-shrink-0"><Truck className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{isDriver ? load.poster || 'Shipper' : load.driver || 'Driver'}</p>
            <p className="text-[10px] text-white/80 truncate">{load.id} • {load.origin?.split(',')[0]} → {load.destination?.split(',')[0]}</p>
          </div>
          <button className="p-1.5 rounded hover:bg-white/10"><Phone className="w-4 h-4" /></button>
          <button onClick={onClose} className="hidden sm:block p-1.5 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>

        {/* Load summary strip */}
        <div className="bg-[#FFB800]/10 border-b border-[#FFB800]/20 px-3 py-1.5 flex items-center justify-between text-[10px] text-[#8B5E00]">
          <span>{load.material} • {load.weight}</span>
          <Badge className="bg-[#00A651] text-white border-0 text-[9px]">₹{load.rate?.toLocaleString('en-IN')}</Badge>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: 'linear-gradient(rgba(229,221,213,.5),rgba(229,221,213,.5)),repeating-linear-gradient(45deg,#efeae2 0,#efeae2 10px,#e8e2d8 10px,#e8e2d8 20px)' }}>
          {messages.map((m, i) => {
            const showDate = i === 0 || dateStr(m.at) !== dateStr(messages[i-1].at);
            return (
              <React.Fragment key={m.id}>
                {showDate && (
                  <div className="text-center my-2">
                    <span className="text-[10px] bg-white/70 text-gray-600 px-2 py-0.5 rounded-full">{dateStr(m.at)}</span>
                  </div>
                )}
                <div className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg shadow-sm ${m.mine ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                    {!m.mine && <p className="text-[10px] font-semibold text-[#075E54] px-2.5 pt-1.5">{m.sender_name}</p>}
                    {m.kind === 'photo' && m.photo && (
                      <img src={m.photo} alt="attached" className="w-full max-h-64 object-cover rounded-t-lg" />
                    )}
                    {m.kind === 'location' && (
                      <div className="p-2.5 border-b border-black/10">
                        <div className="w-full h-24 bg-gradient-to-br from-blue-100 to-green-100 rounded flex items-center justify-center relative">
                          <MapPin className="w-8 h-8 text-red-500" strokeWidth={2.5} fill="currentColor" />
                          <div className="absolute bottom-1 right-1 bg-white/90 rounded px-1.5 py-0.5 text-[9px] font-mono">
                            {m.lat?.toFixed(4)}, {m.lng?.toFixed(4)}
                          </div>
                        </div>
                      </div>
                    )}
                    {m.kind === 'voice' && m.audio && (
                      <VoiceBubble src={m.audio} duration={m.duration} transcript={m.transcript} transcriptLang={m.transcript_lang} mine={m.mine} t={t} />
                    )}
                    {m.kind === 'voice' && m.transcribing && (
                      <p className="text-[10px] text-gray-500 italic px-2.5 pb-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{t('transcribing')}</p>
                    )}
                    {m.kind !== 'voice' && (m.text || m.kind === 'text') && <p className="text-sm text-gray-800 px-2.5 py-1.5 whitespace-pre-line">{m.text}</p>}
                    <div className="flex items-center justify-end gap-1 px-2.5 pb-1">
                      <span className="text-[10px] text-gray-500">{timeStr(m.at)}</span>
                      {m.mine && (m.read ? <CheckCheck className="w-3 h-3 text-[#4FC3F7]" /> : <CheckCheck className="w-3 h-3 text-gray-400" />)}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {messages.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              <p className="text-sm">Start the conversation</p>
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="px-2 py-1.5 bg-white border-t border-gray-100 flex items-center gap-1 text-xs">
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-[#075E54]">
            <Camera className="w-4 h-4" /><span className="hidden sm:inline">Photo</span>
          </button>
          <button onClick={sharePin} className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-[#075E54]">
            <MapPin className="w-4 h-4" /><span className="hidden sm:inline">Location</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => sendPhoto(e.target.files[0])} />
        </div>

        {/* Input */}
        <div className="p-2 bg-gray-100 flex items-center gap-2 relative">
          {recording ? (
            <div data-testid="voice-recording-bar" className="flex-1 flex items-center gap-2 h-10 rounded-full px-4 bg-white border border-red-200">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-sm text-gray-800 font-mono tabular-nums">{fmtElapsed(elapsed)}</span>
              <span className="flex-1 text-xs text-gray-500 truncate">{t('slideToCancel')} ←</span>
              <button
                data-testid="voice-cancel-btn"
                onClick={() => stopRecording(true)}
                className="text-red-500 hover:text-red-700"
                aria-label="Cancel recording"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <input
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendText()}
              placeholder={t('holdToSpeak') ? `Type a message` : 'Type a message'}
              className="flex-1 h-10 rounded-full px-4 text-sm outline-none border-0"
            />
          )}
          {input.trim() && !recording ? (
            <button
              data-testid="chat-send-btn"
              onClick={sendText}
              disabled={sending}
              className="w-10 h-10 rounded-full bg-[#075E54] text-white flex items-center justify-center hover:bg-[#128C7E] disabled:opacity-50 flex-shrink-0"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          ) : (
            <button
              data-testid="chat-mic-btn"
              onMouseDown={(e) => { e.preventDefault(); startRecording(); }}
              onMouseUp={() => recording && stopRecording(false)}
              onMouseLeave={() => recording && stopRecording(false)}
              onTouchStart={onMicTouchStart}
              onTouchMove={onMicTouchMove}
              onTouchEnd={onMicTouchEnd}
              onTouchCancel={() => recording && stopRecording(true)}
              onContextMenu={(e) => e.preventDefault()}
              title={t('holdToSpeak')}
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 select-none transition-all ${recording ? 'bg-red-500 text-white scale-125 shadow-lg' : 'bg-[#075E54] text-white hover:bg-[#128C7E]'}`}
            >
              <Mic className="w-4 h-4" fill={recording ? 'currentColor' : 'none'} />
            </button>
          )}
        </div>
        {!recording && (
          <p className="text-[9px] text-gray-400 text-center pb-1 bg-gray-100 -mt-1">{t('holdToSpeak')}</p>
        )}
      </div>
    </div>
  );
}
